import React, { useEffect, useState, useRef } from 'react';
import { Icons } from '../Icons/Icons';
import './Ambassador.scss';
import { useAmbassadorVisibility } from '../../hooks/useAmbassadorVisibility';
import { TimelineEvent } from '../Timeline/Timeline';
import useWindowDimensions from '../../hooks/useWindowDimentions';
import defaultImage from '../../Assets/default-avatar.webp';

interface AmbassadorProps {
  name: string;
  description: React.ReactNode;
  image: string;
  socialLinks: {
    instagram?: string;
    youtube?: string;
    twitter?: string;
    tiktok?: string;
    twitch?: string;
    linkedin?: string;
  };
  activeEvent: TimelineEvent;
  events: TimelineEvent[];
  onVisibilityChange: (toggleVisibility: () => void) => void;
  showDescription: boolean;
  position: 'left' | 'right';

}

const Ambassador: React.FC<AmbassadorProps> = ({
  name,
  description,
  image,
  socialLinks,
  activeEvent,
  events,
  onVisibilityChange,
  showDescription,
  position = 'right'
}) => {
  const { isHidden, toggleVisibility } = useAmbassadorVisibility(activeEvent, events, showDescription);
  const windowDimensions = useWindowDimensions();
  const [touchStart, setTouchStart] = useState<number | null>(null);
  const [lastTouchY, setLastTouchY] = useState<number | null>(null);
  const descriptionRef = useRef<HTMLDivElement>(null);
  const [isSwiping, setIsSwiping] = useState(false);
  const [isSwipingDescription, setIsSwipingDescription] = useState(false);

  const stableToggleVisibility = React.useCallback(() => {
    toggleVisibility();
  }, [toggleVisibility]);

  useEffect(() => {
    onVisibilityChange(stableToggleVisibility);
  }, [stableToggleVisibility, onVisibilityChange]);

  const handleTouchStart = (e: React.TouchEvent) => {
    if (windowDimensions.width > 768) return;

    const target = e.target as Node;
    const descriptionElement = descriptionRef.current;
    const isDescriptionSwipe = descriptionElement?.contains(target);

    setIsSwipingDescription(isDescriptionSwipe || false);
    setTouchStart(e.touches[0].clientY);
    setLastTouchY(e.touches[0].clientY);
    setIsSwiping(true);
  };

  const handleTouchMove = (e: React.TouchEvent) => {
    if (!touchStart || !lastTouchY || !isSwiping || windowDimensions.width > 768) return;

    const touchY = e.touches[0].clientY;
    const currentDiff = lastTouchY - touchY;
    const totalDiff = touchStart - touchY;

    const descriptionElement = descriptionRef.current;

    if (descriptionElement && isSwipingDescription) {
      const isScrollable = descriptionElement.scrollHeight > descriptionElement.clientHeight;
      const isAtTop = descriptionElement.scrollTop === 0;
      const isAtBottom = Math.abs(
        descriptionElement.scrollHeight - descriptionElement.clientHeight - descriptionElement.scrollTop
      ) < 1;

      if (isScrollable &&
        ((currentDiff > 0 && !isAtBottom) || (currentDiff < 0 && !isAtTop))) {
        e.stopPropagation();
        return;
      }

      if ((isAtTop && currentDiff < 0) || (isAtBottom && currentDiff > 0)) {
        e.preventDefault();
      }
    } else {
      if (totalDiff > 50) {
        e.preventDefault();
        toggleVisibility();
        setIsSwiping(false);
        setTouchStart(null);
        setLastTouchY(null);
        return;
      }
    }

    setLastTouchY(touchY);
  };

  const handleTouchEnd = () => {
    setTouchStart(null);
    setLastTouchY(null);
    setIsSwiping(false);
    setIsSwipingDescription(false);
  };

  if (name === '') {
    return null;
  }

  return (
    <div
      className={`ambassador ${isHidden ? 'hidden' : ''} ${position}`}
      onTouchStart={handleTouchStart}
      onTouchMove={handleTouchMove}
      onTouchEnd={handleTouchEnd}
    >
      <button className="close-button" onClick={toggleVisibility}>×</button>
      {windowDimensions.width > 768 ? (
        <img className="ambassador-image" src={image || defaultImage} alt={name} />
      ) : (
        <div className="ambassador-top-container">
          <img className="ambassador-image" src={image || defaultImage} alt={name} />
          <div className="social-links">
            {socialLinks.instagram && (
              <a href={socialLinks.instagram} target="_blank" rel="noopener noreferrer">
                {Icons.InstagramIcon}
              </a>
            )}
            {socialLinks.youtube && (
              <a href={socialLinks.youtube} target="_blank" rel="noopener noreferrer">
                {Icons.YoutubeIcon}
              </a>
            )}
            {socialLinks.twitter && (
              <a href={socialLinks.twitter} target="_blank" rel="noopener noreferrer">
                {Icons.TwitterIcon}
              </a>
            )}
            {socialLinks.tiktok && (
              <a href={socialLinks.tiktok} target="_blank" rel="noopener noreferrer">
                {Icons.TiktokIcon}
              </a>
            )}
            {socialLinks.twitch && (
              <a href={socialLinks.twitch} target="_blank" rel="noopener noreferrer">
                {Icons.TwitchIcon}
              </a>
            )}
            {socialLinks.linkedin && (
              <a href={socialLinks.linkedin} target="_blank" rel="noopener noreferrer">
                {Icons.LinkedinIcon}
              </a>
            )}
          </div>
        </div>
      )}


      <div className="ambassador-content">
        <div className="ambassador-header">
          <h2>EPOCH AMBASSADOR</h2>
          <h1>{name || 'Your Name'}</h1>
        </div>

        {windowDimensions.width > 768 && (
          <div className="social-links">
            {socialLinks.instagram && (
              <a href={socialLinks.instagram} target={socialLinks.instagram == 'javascript:void(0)' ? '_self' : '_blank'} rel="noopener noreferrer">
                {Icons.InstagramIcon}
              </a>
            )}
            {socialLinks.twitch && (
              <a href={socialLinks.twitch} target="_blank" rel="noopener noreferrer">
                {Icons.TwitchIcon}
              </a>
            )}
            {socialLinks.youtube && (
              <a href={socialLinks.youtube} target={socialLinks.youtube == 'javascript:void(0)' ? '_self' : '_blank'} rel="noopener noreferrer">
                {Icons.YoutubeIcon}
              </a>
            )}
            {socialLinks.twitter && (
              <a href={socialLinks.twitter} target={socialLinks.twitter == 'javascript:void(0)' ? '_self' : '_blank'} rel="noopener noreferrer">
                {Icons.TwitterIcon}
              </a>
            )}
            {socialLinks.tiktok && (
              <a href={socialLinks.tiktok} target={socialLinks.tiktok == 'javascript:void(0)' ? '_self' : '_blank'} rel="noopener noreferrer">
                {Icons.TiktokIcon}
              </a>
            )}
            {socialLinks.linkedin && (
              <a href={socialLinks.linkedin} target={socialLinks.linkedin == 'javascript:void(0)' ? '_self' : '_blank'} rel="noopener noreferrer">
                {Icons.LinkedinIcon}
              </a>
            )}
          </div>
        )}

        <div className="ambassador-description" ref={descriptionRef}>
          {description || 'We can include your bio and social media links here.'}
        </div>

        {windowDimensions.width < 768 && (
          <div className="scroll-hint">
            {Icons.SwipeIcon}
            swipe up to navigate
          </div>
        )}
      </div>
    </div>
  );
};

export default Ambassador;
